import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@breathelife/mui';

import { Button } from '../../../Components/Button/Button';
import { CloseButton } from '../../../Components/Button/CloseButton';
import Typography from '../../../Components/Typography';
import styled from '../../../Styles/themed-styled-components';

const CtaContainer = styled(Box)`
  background-color: ${(props) => props.theme.colors.grey[20]};
`;

const CtaButton = styled(Button)`
  padding: 6px 12px;
`;

const DeleteButton = styled(Button)`
  padding: 6px 12px;
  color: ${(props) => props.theme.colors.error};
  border-color: ${(props) => props.theme.colors.error};
  &:hover {
    border-color: ${(props) => props.theme.colors.error};
    background-color: ${(props) => props.theme.colors.error}20;
  }
`;

type Props = {
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
  isFormValid: boolean;
  isLoading?: boolean;
  title?: string;
  isEditMode: boolean;
};

export function AnnouncementDrawerHeader({
  onClose,
  onSave,
  onDelete,
  isFormValid,
  isLoading,
  title,
  isEditMode,
}: Props): ReactElement {
  const { t } = useTranslation();

  const headerTitle = isEditMode
    ? title || t('admin.announcementsManagement.editAnnouncement')
    : t('admin.announcementsManagement.createAnnouncement');

  return (
    <Box display='flex' flexDirection='column'>
      <Box display='flex' justifyContent='space-between' p={3}>
        <Typography variant='h2'>{headerTitle}</Typography>
        <CloseButton data-testid='announcementDrawer-close' onClick={onClose} />
      </Box>
      <CtaContainer
        width='100%'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        py={2}
        px={2.5}
        mb={2}
      >
        <Box>
          {isEditMode && onDelete && (
            <DeleteButton
              color='inherit'
              variant='outlined'
              onClick={onDelete}
              data-testid='announcementDrawer-delete'
              disabled={isLoading}
            >
              {t('cta.delete')}
            </DeleteButton>
          )}
        </Box>
        <Box display='flex'>
          <Box component='span' mr={1}>
            <CtaButton color='primary' variant='outlined' onClick={onClose} data-testid='announcementDrawer-cancel'>
              {t('cta.cancel')}
            </CtaButton>
          </Box>
          <CtaButton
            data-testid='announcementDrawer-save'
            color='primary'
            variant='contained'
            onClick={onSave}
            disabled={!isFormValid || isLoading}
          >
            {t('cta.save')}
          </CtaButton>
        </Box>
      </CtaContainer>
    </Box>
  );
}
